import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { Eye, EyeOff, UserCheck, TestTube2, CheckCircle } from 'lucide-react'
import { login } from '../../api/auth'

const LoginSuccess = ({ user, onReset }) => (
	<div style={styles.successContainer}>
		<CheckCircle size={48} color='#22c55e' />
		<h2 style={styles.successTitle}>Login Verified!</h2>
		<p style={styles.successSubtitle}>
			The API confirmed the credentials are valid.
		</p>
		<div style={styles.successDetails}>
			<p>
				<strong>Email:</strong> {user.email}
			</p>
			<p>
				<strong>Role:</strong>{' '}
				<span style={styles.roleTag}>{user.role}</span>
			</p>
		</div>
		<button onClick={onReset} style={styles.resetButton}>
			Test Another User
		</button>
	</div>
)

const LoginAsClientTool = () => {
	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		reset,
	} = useForm({
		mode: 'onBlur',
	})

	const [showPassword, setShowPassword] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [loginResult, setLoginResult] = useState(null)

	const handleUseTestUser = () => {
		setValue('email', 'test@test.com', { shouldValidate: true })
		setValue('password', '12345678', { shouldValidate: true })
		toast.success('Test credentials have been filled!')
	}

	const onSubmit = async (data) => {
		setIsSubmitting(true)
		setLoginResult(null)
		try {
			const res = await login(data.email, data.password)

			toast.success(`API call successful for ${res.user.role}!`)

			setLoginResult(res.user)
		} catch (error) {
			const errorMessage =
				error.response?.data?.message || 'An unknown error occurred.'
			toast.error(errorMessage, {
				style: { fontFamily: 'Montserrat' },
			})
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleReset = () => {
		setLoginResult(null)
		reset()
	}

	return (
		<div>
			{loginResult ? (
				<LoginSuccess user={loginResult} onReset={handleReset} />
			) : (
				<>
					<div style={styles.testUserSection}>
						<p style={styles.testUserText}>Quick Test:</p>
						<button
							type='button'
							onClick={handleUseTestUser}
							style={styles.testUserButton}
						>
							<UserCheck
								size={18}
								style={{ marginRight: '8px' }}
							/>
							Use Default Test User
						</button>
					</div>
					<hr style={styles.divider} />
					<form onSubmit={handleSubmit(onSubmit)} style={styles.form}>
						<div style={styles.inputGroup}>
							<label htmlFor='email' style={styles.label}>
								Email Address
							</label>
							<input
								id='email'
								{...register('email', {
									required: 'Email is required.',
								})}
								style={
									errors.email
										? {
												...styles.input,
												...styles.inputError,
											}
										: styles.input
								}
								placeholder='e.g., user@example.com'
							/>
							{errors.email && (
								<p style={styles.errorMessage}>
									{errors.email.message}
								</p>
							)}
						</div>
						<div style={styles.inputGroup}>
							<label htmlFor='password' style={styles.label}>
								Password
							</label>
							<div style={{ position: 'relative' }}>
								<input
									id='password'
									type={showPassword ? 'text' : 'password'}
									{...register('password', {
										required: 'Password is required.',
									})}
									style={
										errors.password
											? {
													...styles.input,
													...styles.inputError,
												}
											: styles.input
									}
									placeholder='Enter password'
								/>
								<button
									type='button'
									onClick={() =>
										setShowPassword(!showPassword)
									}
									style={styles.eyeButton}
								>
									{showPassword ? (
										<EyeOff size={20} />
									) : (
										<Eye size={20} />
									)}
								</button>
							</div>
							{errors.password && (
								<p style={styles.errorMessage}>
									{errors.password.message}
								</p>
							)}
						</div>
						<button
							type='submit'
							style={
								isSubmitting
									? {
											...styles.submitButton,
											...styles.submitButtonDisabled,
										}
									: styles.submitButton
							}
							disabled={isSubmitting}
						>
							{isSubmitting
								? 'Verifying...'
								: 'Test Login Credentials'}
						</button>
					</form>
				</>
			)}
		</div>
	)
}

const styles = {
	testUserSection: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		background: '#f7f8f8',
		padding: '12px 16px',
		borderRadius: '8px',
		marginBottom: '1.5rem',
	},
	testUserText: { margin: 0, fontWeight: 500, color: '#333' },
	testUserButton: {
		display: 'flex',
		alignItems: 'center',
		background: 'transparent',
		border: '1px solid #5BBCEA',
		color: '#5BBCEA',
		padding: '8px 12px',
		borderRadius: '6px',
		cursor: 'pointer',
		fontWeight: '600',
		fontFamily: 'Montserrat, sans-serif',
	},
	divider: {
		border: 'none',
		borderTop: '1px solid #e5e7eb',
		margin: '0 0 1.5rem 0',
	},
	form: { display: 'flex', flexDirection: 'column', gap: '1.25rem' },
	inputGroup: { display: 'flex', flexDirection: 'column' },
	label: {
		marginBottom: '8px',
		fontSize: '14px',
		color: '#606060',
		fontWeight: 500,
	},
	input: {
		width: '100%',
		padding: '0.8rem 1rem',
		fontFamily: 'Montserrat, sans-serif',
		fontSize: '1rem',
		color: '#333',
		borderRadius: '8px',
		background: 'white',
		border: '1px solid #D9D9D9',
	},
	inputError: { border: '1px solid #ef4444' },
	eyeButton: {
		position: 'absolute',
		right: '12px',
		top: '50%',
		transform: 'translateY(-50%)',
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		color: '#808080',
	},
	errorMessage: {
		color: '#ef4444',
		marginTop: '0.5rem',
		fontSize: '14px',
		margin: 0,
	},
	submitButton: {
		color: 'white',
		fontFamily: 'Montserrat, sans-serif',
		background: '#5BBCEA',
		border: 'none',
		padding: '1rem',
		cursor: 'pointer',
		fontWeight: 'bold',
		fontSize: '1rem',
		borderRadius: '8px',
	},
	submitButtonDisabled: { background: '#a0d8f0', cursor: 'not-allowed' },
	successContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
	},
	successTitle: {
		fontSize: '1.75rem',
		fontWeight: 600,
		color: '#333',
		margin: '1rem 0 0.5rem 0',
	},
	successSubtitle: { color: '#606060', margin: '0 0 1.5rem 0' },
	successDetails: {
		background: '#f0f9eb',
		padding: '1rem',
		borderRadius: '8px',
		width: '100%',
		textAlign: 'left',
		marginBottom: '1.5rem',
		borderLeft: '4px solid #22c55e',
	},
	roleTag: {
		backgroundColor: '#dcfce7',
		color: '#166534',
		padding: '0.25rem 0.5rem',
		borderRadius: '4px',
		fontWeight: '600',
		textTransform: 'capitalize',
	},
	resetButton: {
		color: 'white',
		fontFamily: 'Montserrat, sans-serif',
		background: '#5BBCEA',
		border: 'none',
		padding: '0.8rem 1.5rem',
		cursor: 'pointer',
		fontWeight: 'bold',
		fontSize: '1rem',
		borderRadius: '8px',
	},
}

export default LoginAsClientTool
